import React, { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Box, CircularProgress, Typography, Paper, Alert, Button } from '@mui/material';

interface PermissionProtectedRouteProps {
  children: ReactNode;
  requiredPermissions?: string[];
  requireAllPermissions?: boolean; // If true, user must have ALL permissions. If false, user needs ANY permission
  fallbackPath?: string;
  showAccessDenied?: boolean;
}

const PermissionProtectedRoute: React.FC<PermissionProtectedRouteProps> = ({
  children,
  requiredPermissions = [],
  requireAllPermissions = false,
  fallbackPath = '/dashboard',
  showAccessDenied = true
}) => {
  const { user, isLoading, hasPermission, hasAnyPermission } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '200px',
          gap: 2,
        }}
      >
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">
          Loading user permissions...
        </Typography>
      </Box>
    );
  }

  if (!user) {
    // Redirect to login page with return url
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check permissions if any are required
  if (requiredPermissions.length > 0) {
    const hasAccess = requireAllPermissions
      ? requiredPermissions.every(permission => hasPermission(permission))
      : hasAnyPermission(requiredPermissions);

    if (!hasAccess) {
      if (showAccessDenied) {
        return (
          <Box sx={{ p: 3, maxWidth: 600, mx: 'auto', mt: 8, display: 'flex', justifyContent: 'center' }}>
            <Paper sx={{ p: 4, width: '100%', textAlign: 'center' }}>
              <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: '50%',
                    bgcolor: 'warning.light',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'warning.dark',
                  }}
                >
                  <Typography variant="h4">⚠️</Typography>
                </Box>
              </Box>

              <Typography variant="h5" gutterBottom fontWeight="bold">
                Access Restricted
              </Typography>

              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                You do not have permission to view this page. This area is restricted to authorized users only.
              </Typography>

              {user.trade_company_id && user.customer_id && (
                <Alert severity="info" sx={{ mb: 2, textAlign: 'left' }}>
                  You are currently in <strong>{user.active_role === 'trade_company' ? 'Trade' : 'Customer'}</strong> mode.
                  Switch roles to access other features.
                </Alert>
              )}

              <Button
                variant="contained"
                onClick={() => window.location.href = '/dashboard'}
                sx={{ mt: 1 }}
              >
                Return to Dashboard
              </Button>
            </Paper>
          </Box>
        );
      } else {
        return <Navigate to={fallbackPath} replace />;
      }
    }
  }

  return <>{children}</>;
};

export default PermissionProtectedRoute;